import React, { useState, useEffect } from 'react';
import { ArrowLeft, PlusCircle, ChevronLeft, ChevronRight, Loader, Search } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Footer from '../components/Footer';
import Blobs from '../components/Blobs';
import { useTheme } from '../context/ThemeContext';

const PAGE_SIZE = 5;

export default function Story() {
  const { dark } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);
  const [stories, setStories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const location = useLocation();
  
  // Update the page initialization logic
  const initialPage = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pageFromUrl = params.get('page');
    const savedPage = sessionStorage.getItem('lastStoryPage');
    
    if (document.referrer.includes('/story/') && savedPage) {
      return parseInt(savedPage);
    }
    return pageFromUrl ? parseInt(pageFromUrl) : 1;
  }, [location.search]);

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  // Update page persistence logic
  useEffect(() => {
    if (currentPage > 0) {
      sessionStorage.setItem('lastStoryPage', currentPage.toString());
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('page', currentPage.toString());
      navigate(`?${searchParams.toString()}`, { replace: true });
    }
  }, [currentPage, navigate, location.search]);

  useEffect(() => {
    const admin = localStorage.getItem('admin');
    if (admin) {
      setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    const fetchStories = async () => {
      setIsLoading(true);
      try {
        if (isAdmin) {
          // Admin: fetch all columns directly from Supabase
          let query = supabase
            .from('stories')
            .select('*', { count: 'exact' })
            .order('made_on', { ascending: false });
          if (searchQuery) {
            query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
          }
          const { data, error, count } = await query
            .range((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE - 1);
          if (error) {
            console.error('Error fetching stories:', error);
          } else {
            setStories(data);
            setTotalPages(Math.ceil((count ?? 0) / PAGE_SIZE));
          }
        } else {
          // Public: fetch via Netlify Function, fallback to Supabase (public columns only)
          let storiesData = [];
          let totalCount = 0;
          try {
            // Build query params for pagination and search
            const params = new URLSearchParams();
            params.set('order', 'made_on.desc');
            params.set('limit', PAGE_SIZE.toString());
            params.set('offset', ((currentPage - 1) * PAGE_SIZE).toString());
            if (searchQuery) {
              params.set('or', `title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
            }
            const res = await fetch(`/api/getStories?${params.toString()}`);
            if (!res.ok) throw new Error('Failed to fetch stories');
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              throw new Error('Not a JSON response');
            }
            const result = await res.json();
            storiesData = result.data || result || [];
            totalCount = result.count ?? storiesData.length;
          } catch (e) {
            // Fallback: fetch direct from Supabase (local dev, only public columns)
            try {
              let query = supabase
                .from('stories')
                .select('id, title, content, made_on, url_links, picture', { count: 'exact' })
                .order('made_on', { ascending: false })
                .order('title', { ascending: true });
              if (searchQuery) {
                query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
              }
              const { data, error, count } = await query
                .range((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE - 1);
              if (error) throw error;
              storiesData = data || [];
              totalCount = count ?? storiesData.length;
            } catch (err) {
              console.error('Error fetching stories (fallback):', err);
              setStories([]);
              setTotalPages(1);
              setIsLoading(false);
              setIsSearching(false);
              return;
            }
          }
          setStories(storiesData);
          setTotalPages(Math.ceil((totalCount ?? 0) / PAGE_SIZE));
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    };
    // Debounce search
    const timeoutId = setTimeout(() => {
      fetchStories();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [currentPage, searchQuery, isAdmin]);

  const handleCreateStory = () => {
    navigate('/admin/create-story');
  };

  const handlePreviousPage = () => {
    if (currentPage > 1 && !isLoading) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages && !isLoading) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Add search handler
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setIsSearching(true);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Fungsi untuk memilih emoji otomatis berdasarkan judul
  function getStoryEmojiByTitle(title: string): string {
    const lower = title.toLowerCase();
    if (lower.includes('guk') || lower.includes('dog') || lower.includes('anjing')) return '🐶';
    if (lower.includes('meow') || lower.includes('miau') || lower.includes('cat') || lower.includes('kucing')) return '🐱';
    if (lower.includes('quack') || lower.includes('duck') || lower.includes('bebek')) return '🦆';
    if (lower.includes('selamat jalan') || lower.includes('goodbye') || lower.includes('bye')) return '👋';
    if (lower.includes('love') || lower.includes('cinta') || lower.includes('heart')) return '❤️';
    if (lower.includes('birthday') || lower.includes('ulang tahun')) return '🎂';
    if (lower.includes('school') || lower.includes('sekolah')) return '🏫';
    if (lower.includes('holiday') || lower.includes('libur')) return '🏖️';
    // Default
    return '📖';
  }

  // Enhanced getPageNumbers function
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 3;
    
    // Always add first page
    pageNumbers.push(1);
    
    if (currentPage > 3) {
      pageNumbers.push('...');
    }
    
    // Calculate range around current page
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    
    // Adjust range for edge cases
    if (currentPage <= 3) {
      end = Math.min(totalPages - 1, maxVisiblePages + 1);
    }
    if (currentPage >= totalPages - 2) {
      start = Math.max(2, totalPages - maxVisiblePages);
    }
    
    // Add page numbers around current page
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    
    if (currentPage < totalPages - 2) {
      pageNumbers.push('...');
    }
    
    // Always add last page if there's more than one page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col justify-start items-center pt-16 pb-10 bg-transparent overflow-hidden">
      {/* Blobs full width, flexible for all screen sizes */}
      <div className="absolute inset-0 w-full h-full pointer-events-none -z-10">
        <Blobs dark={dark} />
      </div>
      <Link
        to="/"
        className="inline-flex items-center gap-2 mb-8 transition-colors text-lg
          text-[#232634] dark:text-[#f9e2af] font-bold
          px-3 py-1 rounded-lg bg-white/40 dark:bg-[#232634]/60 shadow-sm backdrop-blur-sm
          border border-[#e0d7d2] dark:border-[#45475a] sm:static top-4 left-2 z-20
          focus:outline-none focus:ring-2 focus:ring-[#b4befe] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#181825]"
        style={{ maxWidth: 'fit-content' }}
      >
        <ArrowLeft size={20} />
        <span>Back to Home</span>
      </Link>

      {/* Hapus logo dan judul My Story */}

      {/* Add search bar for admin */}
      {isAdmin && (
        <div className="mb-8 flex justify-between items-center gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search stories..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full bg-white dark:bg-gray-800 text-[#232634] dark:text-white placeholder:text-gray-400 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          </div>
          <button
            onClick={handleCreateStory}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-800 px-4 py-2 rounded-full hover:from-green-700 hover:to-green-900 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
          >
            <PlusCircle size={20} />
            <span>Create Story</span>
          </button>
        </div>
      )}
      <div className="prose prose-invert max-w-none space-y-6 relative w-full px-2">
        {(isLoading || isSearching) && (
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="flex items-center gap-2">
              <Loader className="animate-spin" size={24} />
              <span>{isSearching ? 'Searching...' : 'Loading stories...'}</span>
            </div>
          </div>
        )}
        {stories.map(story => (
          <Link
            key={story.id}
            to={`/story/${btoa(story.id)}`}
            className="group block rounded-2xl p-4 md:p-6 bg-[rgba(242,205,205,0.75)] dark:bg-[#232634]/80 mx-auto w-full max-w-xs sm:max-w-md md:max-w-xl hover:scale-105 transition-all relative overflow-hidden border-2 border-[rgba(242,205,205,0.45)] dark:border-[#232634]"
            style={{
              boxShadow: `0 0 0 4px ${dark ? 'rgba(249,226,175,0.18)' : 'rgba(242,205,205,0.18)'}${story.id ? '' : ''}`,
              ...(dark
                ? { filter: 'drop-shadow(0 0 12px #f9e2af44)' }
                : { filter: 'drop-shadow(0 0 16px #f2cdcd44)' })
            }}
          >
            {/* Border reflection/glow effect only */}
            <div className="absolute inset-0 pointer-events-none z-0">
              <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-[#b4befe] group-hover:shadow-[0_0_24px_4px_rgba(180,190,254,0.25)] transition-all duration-300" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{story.emoji || getStoryEmojiByTitle(story.title)}</span>
                <h3 className="text-xl md:text-2xl font-semibold text-[#232634] dark:text-[#f9e2af]">{story.title}</h3>
              </div>
              <p className="text-gray-400 text-sm mb-2">{story.made_on}</p>
              <div className="text-[#232634] dark:text-[#a6adc8]">
                {story.content ? (
                  <>
                    <p className="line-clamp-2">{story.content}</p>
                    <span className="text-blue-500 mt-2 inline-block hover:underline font-bold">
                      Read more...
                    </span>
                  </>
                ) : (
                  <p className="text-gray-400 italic">
                    Click to read this story...
                  </p>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
      {/* Pagination sesuai mockup: bulat, spacing rapat, current page bold/kontras, tanpa border/hover berlebihan */}
      <div className="flex justify-center items-center gap-2 mt-8 mb-8">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage <= 1 || isLoading}
          className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-transparent"
        >
          <ChevronLeft size={20} />
        </button>
        {getPageNumbers().map((pageNum, index) => (
          <React.Fragment key={index}>
            {pageNum === '...' ? (
              <span className="w-9 h-9 flex items-center justify-center text-gray-400">...</span>
            ) : (
              <button
                onClick={() => setCurrentPage(Number(pageNum))}
                disabled={isLoading || pageNum === currentPage}
                className={`w-9 h-9 flex items-center justify-center rounded-full text-base font-semibold
                  ${pageNum === currentPage
                    ? 'bg-blue-500 text-white shadow-lg scale-110'
                    : 'bg-transparent text-gray-400 hover:text-white hover:bg-gray-800'}
                  transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {pageNum}
              </button>
            )}
          </React.Fragment>
        ))}
        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages || isLoading}
          className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-transparent"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      <Footer />
    </div>
  );
}